// File: webelieve-frontend/src/features/intake/components/QuickIntakeModal.tsx
// EF-2.4: Quick Intake modal for phone/walk-in leads

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import {
  intakeCrmApi,
  type CourtRegistryCourt,
  type CourtRegistryState,
} from '../api/intakeCrmApi';
import type { LeadMatterPlan } from '../lib/leadIntelligence';
import {
  ensureLeadCaseModules,
  LEAD_CASE_MODULE_DEFINITION_MAP,
  resolveLeadCaseModuleKeys,
  sanitizeLeadCaseModules,
  type LeadCaseModuleFieldValue,
  type LeadCaseModules,
} from '../lib/leadCaseModules';

interface QuickIntakeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface QuickIntakeFormState {
  name: string;
  preferredName: string;
  email: string;
  phone: string;
  alternatePhone: string;
  practiceAreasText: string;
  matterSummary: string;
  source: string;
  stateCode: string;
  jurisdictionCode: string;
  incidentDate: string;
  incidentLocation: string;
  preferredContactMethod: string;
  preferredContactTime: string;
  language: string;
  interpreterNeeded: boolean;
  dateOfBirth: string;
  maritalStatus: string;
  educationLevel: string;
  numberOfDependents: string;
  numberOfKids: string;
  employed: '' | 'yes' | 'no';
  employerName: string;
  jobTitle: string;
  opposingPartyNames: string;
  currentOrPriorAttorney: string;
  spouseOrPartnerName: string;
  caseModules: LeadCaseModules;
  matterPlans: LeadMatterPlan[];
  homeAddress: {
    street1: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

const INITIAL_FORM: QuickIntakeFormState = {
  name: '',
  preferredName: '',
  email: '',
  phone: '',
  alternatePhone: '',
  practiceAreasText: '',
  matterSummary: '',
  source: 'phone',
  stateCode: '',
  jurisdictionCode: '',
  incidentDate: '',
  incidentLocation: '',
  preferredContactMethod: 'phone',
  preferredContactTime: '',
  language: 'English',
  interpreterNeeded: false,
  dateOfBirth: '',
  maritalStatus: '',
  educationLevel: '',
  numberOfDependents: '',
  numberOfKids: '',
  employed: '',
  employerName: '',
  jobTitle: '',
  opposingPartyNames: '',
  currentOrPriorAttorney: '',
  spouseOrPartnerName: '',
  caseModules: {},
  matterPlans: [],
  homeAddress: {
    street1: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'USA',
  },
};

const EDUCATION_LEVELS = [
  'High school',
  'Associate degree',
  'Bachelor degree',
  'Graduate degree',
  'Professional degree',
  'Trade / vocational',
  'Prefer not to say',
];

const MARITAL_STATUSES = [
  'Single',
  'Married',
  'Separated',
  'Divorced',
  'Widowed',
  'Domestic partnership',
];

const QuickIntakeModal: React.FC<QuickIntakeModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [form, setForm] = useState<QuickIntakeFormState>(INITIAL_FORM);
  const [states, setStates] = useState<CourtRegistryState[]>([]);
  const [courts, setCourts] = useState<CourtRegistryCourt[]>([]);
  const [matterPlanCourtsByState, setMatterPlanCourtsByState] = useState<Record<string, CourtRegistryCourt[]>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setError(null);
  };

  const updateField = <K extends keyof QuickIntakeFormState>(field: K, value: QuickIntakeFormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateAddressField = (field: keyof QuickIntakeFormState['homeAddress'], value: string) => {
    setForm(prev => ({
      ...prev,
      homeAddress: {
        ...prev.homeAddress,
        [field]: value,
      },
    }));
  };

  const updateCaseModuleField = (moduleKey: keyof LeadCaseModules, fieldKey: string, value: LeadCaseModuleFieldValue) => {
    setForm(prev => ({
      ...prev,
      caseModules: {
        ...prev.caseModules,
        [moduleKey]: {
          ...(prev.caseModules[moduleKey] || {}),
          [fieldKey]: value,
        },
      },
    }));
  };

  const updateMatterPlan = (practiceArea: string, patch: Partial<LeadMatterPlan>) => {
    setForm((prev) => ({
      ...prev,
      matterPlans: prev.matterPlans.map((plan) => (
        plan.practiceArea === practiceArea
          ? {
              ...plan,
              ...patch,
            }
          : plan
      )),
    }));
  };

  const parseOptionalNumber = (value: string): number | undefined => {
    if (!value.trim()) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const parsePracticeAreas = (value: string): string[] =>
    Array.from(new Set(value.split(',').map(part => part.trim()).filter(Boolean)));

  const ensureMatterPlans = (practiceAreas: string[], existingPlans: LeadMatterPlan[], defaults: QuickIntakeFormState): LeadMatterPlan[] =>
    practiceAreas.map((practiceArea) => {
      const existing = existingPlans.find((plan) => plan.practiceArea === practiceArea);
      return {
        practiceArea,
        title: existing?.title || practiceArea,
        summary: existing?.summary || defaults.matterSummary || '',
        stateCode: existing?.stateCode || defaults.stateCode || '',
        jurisdictionCode: existing?.jurisdictionCode || defaults.jurisdictionCode || '',
        jurisdictionCourtName: existing?.jurisdictionCourtName || undefined,
        jurisdiction: existing?.jurisdiction || undefined,
        incidentDate: existing?.incidentDate || defaults.incidentDate || '',
        incidentLocation: existing?.incidentLocation || defaults.incidentLocation || '',
      };
    });

  const selectedModuleKeys = resolveLeadCaseModuleKeys(parsePracticeAreas(form.practiceAreasText));

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      caseModules: ensureLeadCaseModules(selectedModuleKeys, prev.caseModules),
      matterPlans: ensureMatterPlans(parsePracticeAreas(prev.practiceAreasText), prev.matterPlans, prev),
    }));
  }, [form.practiceAreasText]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    intakeCrmApi.listJurisdictionStates()
      .then((items) => {
        if (!cancelled) {
          setStates(items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStates([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!form.stateCode) {
      setCourts([]);
      if (form.jurisdictionCode) {
        updateField('jurisdictionCode', '');
      }
      return;
    }

    let cancelled = false;
    intakeCrmApi.listCourtsByState(form.stateCode)
      .then((items) => {
        if (!cancelled) {
          setCourts(items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCourts([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.stateCode]);

  useEffect(() => {
    const stateCodes = Array.from(new Set(form.matterPlans.map((plan) => plan.stateCode).filter(Boolean))) as string[];
    const missingStateCodes = stateCodes.filter((stateCode) => !matterPlanCourtsByState[stateCode]);
    if (missingStateCodes.length === 0) return;

    let cancelled = false;
    Promise.all(missingStateCodes.map(async (stateCode) => ({
      stateCode,
      courts: await intakeCrmApi.listCourtsByState(stateCode),
    })))
      .then((results) => {
        if (cancelled) return;
        setMatterPlanCourtsByState((prev) => {
          const next = { ...prev };
          results.forEach(({ stateCode, courts: stateCourts }) => {
            next[stateCode] = stateCourts;
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setMatterPlanCourtsByState((prev) => ({ ...prev }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.matterPlans, matterPlanCourtsByState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Client name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const practiceAreas = parsePracticeAreas(form.practiceAreasText);
      const moduleKeys = resolveLeadCaseModuleKeys(practiceAreas);
      const selectedCourt = courts.find((court) => court.jurisdiction_code === form.jurisdictionCode);
      const matterPlans = ensureMatterPlans(practiceAreas, form.matterPlans, form).map((plan) => {
        const selectedPlanCourt = (matterPlanCourtsByState[plan.stateCode || ''] || [])
          .find((court) => court.jurisdiction_code === plan.jurisdictionCode);
        return {
          practice_area: plan.practiceArea,
          title: plan.title?.trim() || plan.practiceArea,
          summary: plan.summary?.trim() || undefined,
          state_code: plan.stateCode || undefined,
          jurisdiction_code: plan.jurisdictionCode || undefined,
          jurisdiction_court_name: selectedPlanCourt?.court_name || plan.jurisdictionCourtName || undefined,
          jurisdiction: selectedPlanCourt?.court_name || plan.jurisdiction || plan.jurisdictionCode || undefined,
          incident_date: plan.incidentDate || undefined,
          incident_location: plan.incidentLocation?.trim() || undefined,
        };
      });
      await intakeCrmApi.createLead({
        name: form.name.trim(),
        preferred_name: form.preferredName.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        alternate_phone: form.alternatePhone.trim() || undefined,
        practice_area: practiceAreas[0] || undefined,
        practice_areas: practiceAreas.length > 0 ? practiceAreas : undefined,
        notes: form.matterSummary.trim() || undefined,
        matter_summary: form.matterSummary.trim() || undefined,
        source: form.source,
        state_code: form.stateCode || undefined,
        jurisdiction_code: form.jurisdictionCode || undefined,
        jurisdiction_court_name: selectedCourt?.court_name || undefined,
        jurisdiction: selectedCourt?.court_name || form.jurisdictionCode || undefined,
        incident_date: form.incidentDate || undefined,
        incident_location: form.incidentLocation.trim() || undefined,
        preferred_contact_method: form.preferredContactMethod || undefined,
        preferred_contact_time: form.preferredContactTime.trim() || undefined,
        language: form.language.trim() || undefined,
        interpreter_needed: form.interpreterNeeded,
        date_of_birth: form.dateOfBirth || undefined,
        marital_status: form.maritalStatus || undefined,
        education_level: form.educationLevel || undefined,
        number_of_dependents: parseOptionalNumber(form.numberOfDependents),
        number_of_kids: parseOptionalNumber(form.numberOfKids),
        employed: form.employed === '' ? undefined : form.employed === 'yes',
        employer_name: form.employerName.trim() || undefined,
        job_title: form.jobTitle.trim() || undefined,
        opposing_party_names: form.opposingPartyNames.trim() || undefined,
        current_or_prior_attorney: form.currentOrPriorAttorney.trim() || undefined,
        spouse_or_partner_name: form.spouseOrPartnerName.trim() || undefined,
        case_modules: sanitizeLeadCaseModules(form.caseModules, moduleKeys),
        matter_plans: matterPlans,
        home_address: Object.values(form.homeAddress).some(Boolean)
          ? {
              street1: form.homeAddress.street1.trim() || undefined,
              street2: form.homeAddress.street2.trim() || undefined,
              city: form.homeAddress.city.trim() || undefined,
              state: form.homeAddress.state.trim() || undefined,
              postalCode: form.homeAddress.postalCode.trim() || undefined,
              country: form.homeAddress.country.trim() || undefined,
            }
          : undefined,
      });
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error('[QuickIntakeModal] Create lead failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val: boolean) => { onOpenChange(val); if (!val) resetForm(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quick Intake</DialogTitle>
          <DialogDescription>
            Capture the baseline lead profile so attorneys can see a complete intake journey from the start.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="text-sm text-lex-danger bg-lex-danger-bg rounded p-2">
              {error}
            </div>
          )}

          <section className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Identity & Contact</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Core details for conflict checks, outreach, and profiling.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Client Name *">
                <input
                  type="text"
                  value={form.name}
                  onChange={e => updateField('name', e.target.value)}
                  placeholder="Full legal name"
                  autoFocus
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Preferred Name">
                <input
                  type="text"
                  value={form.preferredName}
                  onChange={e => updateField('preferredName', e.target.value)}
                  placeholder="Nickname or preferred name"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={e => updateField('email', e.target.value)}
                  placeholder="email@example.com"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => updateField('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Alternate Phone">
                <input
                  type="tel"
                  value={form.alternatePhone}
                  onChange={e => updateField('alternatePhone', e.target.value)}
                  placeholder="Backup phone number"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Date of Birth">
                <input
                  type="date"
                  value={form.dateOfBirth}
                  onChange={e => updateField('dateOfBirth', e.target.value)}
                  className={inputClassName}
                />
              </FormField>
            </div>
          </section>

          <section className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Matter Basics</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">The minimum issue framing the AI and attorneys should see on day one.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Practice Area">
                <input
                  type="text"
                  value={form.practiceAreasText}
                  onChange={e => updateField('practiceAreasText', e.target.value)}
                  placeholder="Family Law, Civil Law"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Source">
                <select
                  value={form.source}
                  onChange={e => updateField('source', e.target.value)}
                  className={inputClassName}
                >
                  <option value="phone">Phone Call</option>
                  <option value="walk_in">Walk-In</option>
                  <option value="referral">Referral</option>
                  <option value="email">Email</option>
                  <option value="website">Website</option>
                  <option value="other">Other</option>
                </select>
              </FormField>
              <FormField label="State">
                <select
                  value={form.stateCode}
                  onChange={e => {
                    updateField('stateCode', e.target.value);
                    updateField('jurisdictionCode', '');
                  }}
                  className={inputClassName}
                >
                  <option value="">Select a state</option>
                  {states.map(state => (
                    <option key={state.state_code} value={state.state_code}>
                      {state.state_code} ({state.court_count} courts)
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Court Jurisdiction">
                <select
                  value={form.jurisdictionCode}
                  onChange={e => updateField('jurisdictionCode', e.target.value)}
                  disabled={!form.stateCode}
                  className={inputClassName}
                >
                  <option value="">
                    {!form.stateCode ? 'Select a state first' : courts.length === 0 ? 'No courts found' : 'Select a court'}
                  </option>
                  {courts.map(court => (
                    <option key={court.jurisdiction_code} value={court.jurisdiction_code}>
                      {court.court_name} ({court.jurisdiction_code})
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Incident / Event Date">
                <input
                  type="date"
                  value={form.incidentDate}
                  onChange={e => updateField('incidentDate', e.target.value)}
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Incident / Dispute Location">
                <input
                  type="text"
                  value={form.incidentLocation}
                  onChange={e => updateField('incidentLocation', e.target.value)}
                  placeholder="City, county, state"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Current / Prior Attorney">
                <input
                  type="text"
                  value={form.currentOrPriorAttorney}
                  onChange={e => updateField('currentOrPriorAttorney', e.target.value)}
                  placeholder="Attorney or firm name"
                  className={inputClassName}
                />
              </FormField>
            </div>
            <FormField label="Matter Summary">
              <textarea
                value={form.matterSummary}
                onChange={e => updateField('matterSummary', e.target.value)}
                placeholder="Brief description of the dispute, goals, timeline, and key facts..."
                rows={4}
                className={inputClassName}
              />
            </FormField>
          </section>

          {form.matterPlans.length > 0 && (
            <section className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Potential Matters</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  One lead can branch into multiple matters. Each matter can only have one jurisdiction.
                </p>
              </div>
              <div className="space-y-4">
                {form.matterPlans.map((plan) => {
                  const planCourts = matterPlanCourtsByState[plan.stateCode || ''] || [];
                  return (
                    <div key={plan.practiceArea} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{plan.practiceArea}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Matter-specific jurisdiction and summary</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <FormField label="Matter Title">
                          <input
                            type="text"
                            value={plan.title || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, { title: e.target.value })}
                            className={inputClassName}
                          />
                        </FormField>
                        <FormField label="State">
                          <select
                            value={plan.stateCode || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, {
                              stateCode: e.target.value,
                              jurisdictionCode: '',
                              jurisdictionCourtName: undefined,
                              jurisdiction: undefined,
                            })}
                            className={inputClassName}
                          >
                            <option value="">Select a state</option>
                            {states.map((state) => (
                              <option key={state.state_code} value={state.state_code}>
                                {state.state_code} ({state.court_count} courts)
                              </option>
                            ))}
                          </select>
                        </FormField>
                        <FormField label="Court Jurisdiction">
                          <select
                            value={plan.jurisdictionCode || ''}
                            onChange={(e) => {
                              const selectedPlanCourt = planCourts.find((court) => court.jurisdiction_code === e.target.value);
                              updateMatterPlan(plan.practiceArea, {
                                jurisdictionCode: e.target.value,
                                jurisdictionCourtName: selectedPlanCourt?.court_name,
                                jurisdiction: selectedPlanCourt?.court_name || e.target.value,
                              });
                            }}
                            disabled={!plan.stateCode}
                            className={inputClassName}
                          >
                            <option value="">
                              {!plan.stateCode ? 'Select a state first' : planCourts.length === 0 ? 'No courts found' : 'Select a court'}
                            </option>
                            {planCourts.map((court) => (
                              <option key={court.jurisdiction_code} value={court.jurisdiction_code}>
                                {court.court_name} ({court.jurisdiction_code})
                              </option>
                            ))}
                          </select>
                        </FormField>
                        <FormField label="Incident / Event Date">
                          <input
                            type="date"
                            value={plan.incidentDate || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, { incidentDate: e.target.value })}
                            className={inputClassName}
                          />
                        </FormField>
                        <FormField label="Incident / Dispute Location">
                          <input
                            type="text"
                            value={plan.incidentLocation || ''}
                            onChange={(e) => updateMatterPlan(plan.practiceArea, { incidentLocation: e.target.value })}
                            placeholder="City, county, state"
                            className={inputClassName}
                          />
                        </FormField>
                      </div>
                      <FormField label="Matter-Specific Summary">
                        <textarea
                          value={plan.summary || ''}
                          onChange={(e) => updateMatterPlan(plan.practiceArea, { summary: e.target.value })}
                          rows={3}
                          className={inputClassName}
                        />
                      </FormField>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {selectedModuleKeys.map((moduleKey) => {
            const moduleDefinition = LEAD_CASE_MODULE_DEFINITION_MAP[moduleKey];
            const moduleValues = form.caseModules[moduleKey] || {};
            return (
              <section key={moduleKey} className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{moduleDefinition.label}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{moduleDefinition.description}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {moduleDefinition.fields.map((field) => (
                    <FormField key={field.key} label={field.label}>
                      {field.input === 'textarea' ? (
                        <textarea
                          value={String(moduleValues[field.key] || '')}
                          onChange={e => updateCaseModuleField(moduleKey, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          rows={field.rows || 3}
                          className={inputClassName}
                        />
                      ) : field.input === 'checkbox' ? (
                        <div className="flex items-center gap-2 pt-2">
                          <input
                            id={`quick_${moduleKey}_${field.key}`}
                            type="checkbox"
                            checked={Boolean(moduleValues[field.key])}
                            onChange={e => updateCaseModuleField(moduleKey, field.key, e.target.checked)}
                            className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                          />
                          <label htmlFor={`quick_${moduleKey}_${field.key}`} className="text-sm text-gray-700 dark:text-gray-300">
                            Yes
                          </label>
                        </div>
                      ) : (
                        <input
                          type={field.input}
                          value={field.input === 'number' ? String(moduleValues[field.key] ?? '') : String(moduleValues[field.key] || '')}
                          onChange={e => updateCaseModuleField(
                            moduleKey,
                            field.key,
                            field.input === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                          )}
                          placeholder={field.placeholder}
                          className={inputClassName}
                        />
                      )}
                    </FormField>
                  ))}
                </div>
              </section>
            );
          })}

          <section className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Address & Communication Preferences</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Contact preferences help routing, outreach, and accessibility planning.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Street Address">
                <input
                  type="text"
                  value={form.homeAddress.street1}
                  onChange={e => updateAddressField('street1', e.target.value)}
                  placeholder="123 Main St"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Address Line 2">
                <input
                  type="text"
                  value={form.homeAddress.street2}
                  onChange={e => updateAddressField('street2', e.target.value)}
                  placeholder="Apt, suite, unit"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="City">
                <input
                  type="text"
                  value={form.homeAddress.city}
                  onChange={e => updateAddressField('city', e.target.value)}
                  placeholder="City"
                  className={inputClassName}
                />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="State">
                  <input
                    type="text"
                    value={form.homeAddress.state}
                    onChange={e => updateAddressField('state', e.target.value)}
                    placeholder="CA"
                    className={inputClassName}
                  />
                </FormField>
                <FormField label="Postal Code">
                  <input
                    type="text"
                    value={form.homeAddress.postalCode}
                    onChange={e => updateAddressField('postalCode', e.target.value)}
                    placeholder="92101"
                    className={inputClassName}
                  />
                </FormField>
              </div>
              <FormField label="Country">
                <input
                  type="text"
                  value={form.homeAddress.country}
                  onChange={e => updateAddressField('country', e.target.value)}
                  placeholder="USA"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Preferred Contact Method">
                <select
                  value={form.preferredContactMethod}
                  onChange={e => updateField('preferredContactMethod', e.target.value)}
                  className={inputClassName}
                >
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="text">Text</option>
                  <option value="portal">Client Portal</option>
                </select>
              </FormField>
              <FormField label="Preferred Contact Time">
                <input
                  type="text"
                  value={form.preferredContactTime}
                  onChange={e => updateField('preferredContactTime', e.target.value)}
                  placeholder="Morning, after 5pm, lunch hour"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Primary Language">
                <input
                  type="text"
                  value={form.language}
                  onChange={e => updateField('language', e.target.value)}
                  placeholder="English, Spanish, etc."
                  className={inputClassName}
                />
              </FormField>
              <div className="flex items-center gap-2 pt-6">
                <input
                  id="interpreter_needed"
                  type="checkbox"
                  checked={form.interpreterNeeded}
                  onChange={e => updateField('interpreterNeeded', e.target.checked)}
                  className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                />
                <label htmlFor="interpreter_needed" className="text-sm text-gray-700 dark:text-gray-300">
                  Interpreter needed
                </label>
              </div>
            </div>
          </section>

          <section className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Household, Education & Employment</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">These details help the intake team understand family dynamics, capacity, and case economics.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Marital Status">
                <select
                  value={form.maritalStatus}
                  onChange={e => updateField('maritalStatus', e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select status</option>
                  {MARITAL_STATUSES.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Education Level">
                <select
                  value={form.educationLevel}
                  onChange={e => updateField('educationLevel', e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select level</option>
                  {EDUCATION_LEVELS.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Number of Dependents">
                <input
                  type="number"
                  min={0}
                  value={form.numberOfDependents}
                  onChange={e => updateField('numberOfDependents', e.target.value)}
                  placeholder="0"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Number of Kids">
                <input
                  type="number"
                  min={0}
                  value={form.numberOfKids}
                  onChange={e => updateField('numberOfKids', e.target.value)}
                  placeholder="0"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Currently Employed">
                <select
                  value={form.employed}
                  onChange={e => updateField('employed', e.target.value as QuickIntakeFormState['employed'])}
                  className={inputClassName}
                >
                  <option value="">Select employment status</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </FormField>
              <FormField label="Employer Name">
                <input
                  type="text"
                  value={form.employerName}
                  onChange={e => updateField('employerName', e.target.value)}
                  placeholder="Employer or company"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Job Title / Occupation">
                <input
                  type="text"
                  value={form.jobTitle}
                  onChange={e => updateField('jobTitle', e.target.value)}
                  placeholder="Occupation"
                  className={inputClassName}
                />
              </FormField>
              <FormField label="Spouse / Partner Name">
                <input
                  type="text"
                  value={form.spouseOrPartnerName}
                  onChange={e => updateField('spouseOrPartnerName', e.target.value)}
                  placeholder="Spouse, partner, or ex-spouse"
                  className={inputClassName}
                />
              </FormField>
            </div>
          </section>

          <section className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Conflict Seeds & Related Parties</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Capture the first-pass names that matter for conflict checks and case framing.</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <FormField label="Opposing Party Names">
                <textarea
                  value={form.opposingPartyNames}
                  onChange={e => updateField('opposingPartyNames', e.target.value)}
                  placeholder="Opposing parties, companies, or agencies involved"
                  rows={3}
                  className={inputClassName}
                />
              </FormField>
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={saving || !form.name.trim()}
              className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Creating...' : 'Create Lead'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputClassName = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-lex-accent';

export default QuickIntakeModal;
